import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const TidioManager: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    let tidioCheckInterval: NodeJS.Timeout | null = null;

    // Function to hide Tidio
    const hideTidio = () => {
      if (window.tidioChatApi) {
        window.tidioChatApi.hide();
        // Also hide the iframe directly as a fallback
        const tidioFrame = document.querySelector('#tidio-chat-iframe') as HTMLElement;
        if (tidioFrame) {
          tidioFrame.style.display = 'none';
        }
      }
      // Hide the widget container as well
      const tidioWidget = document.querySelector('[id*="tidio"]') as HTMLElement;
      if (tidioWidget) {
        tidioWidget.style.display = 'none';
      }
    };

    // Function to show Tidio
    const showTidio = () => {
      if (window.tidioChatApi) {
        window.tidioChatApi.show();
        // Also show the iframe directly as a fallback
        const tidioFrame = document.querySelector('#tidio-chat-iframe') as HTMLElement;
        if (tidioFrame) {
          tidioFrame.style.display = 'block';
        }
      }
      // Show the widget container as well
      const tidioWidget = document.querySelector('[id*="tidio"]') as HTMLElement;
      if (tidioWidget) {
        tidioWidget.style.display = 'block';
      }
    };

    // Check initial auth state
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsLoggedIn(!!session);

      if (session) {
        // If logged in, hide Tidio immediately and keep checking
        hideTidio();

        // Set up interval to keep checking and hiding Tidio
        tidioCheckInterval = setInterval(() => {
          hideTidio();
        }, 1000); // Check every second

        // Stop checking after 10 seconds (Tidio should be loaded by then)
        setTimeout(() => {
          if (tidioCheckInterval) {
            clearInterval(tidioCheckInterval);
            tidioCheckInterval = null;
          }
        }, 10000);
      } else {
        // If logged out, show Tidio
        showTidio();
      }
    };

    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsLoggedIn(!!session);

      // Clear any existing interval
      if (tidioCheckInterval) {
        clearInterval(tidioCheckInterval);
        tidioCheckInterval = null;
      }

      if (session) {
        // Hide Tidio when logged in
        hideTidio();

        // Set up interval to keep checking and hiding Tidio
        tidioCheckInterval = setInterval(() => {
          hideTidio();
        }, 1000);

        // Stop checking after 10 seconds
        setTimeout(() => {
          if (tidioCheckInterval) {
            clearInterval(tidioCheckInterval);
            tidioCheckInterval = null;
          }
        }, 10000);
      } else {
        // Show Tidio when logged out
        showTidio();
      }
    });

    return () => {
      subscription.unsubscribe();
      if (tidioCheckInterval) {
        clearInterval(tidioCheckInterval);
      }
    };
  }, []);

  // Add CSS to forcefully hide Tidio for logged-in users
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'tidio-manager-styles';

    if (isLoggedIn) {
      style.textContent = `
        #tidio-chat-iframe,
        [id*="tidio"],
        .tidio-chat-container,
        iframe[title*="Tidio"],
        iframe[src*="tidio"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById('tidio-manager-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [isLoggedIn]);

  return null;
};